import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { rankWith, schemaMatches, type ControlProps } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { useTouched } from "@amzn/fin-tech-cloudscape-json-forms";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import {
  Box,
  FileUpload,
  FileUploadProps,
  FormField,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-console";

import {
  transformSerializedFilesToFiles,
  validateAndUploadFilesToS3,
} from "src/forms/FileUpload/FileUploadUtilities";
import type { SerializedFile } from "src/forms/FileUpload/types";
import { attachmentFieldNames, S3_UPLOAD_EXTENSION } from "../attachments";
import { useS3AttachmentTarget } from "./useS3AttachmentTarget";

const MISSING_TARGET_ERROR =
  "Attachments are unavailable for this form. Please start the form again.";

const UPLOAD_IN_PROGRESS_ERROR =
  "Please wait for the current files to finish uploading before attempting to upload other files";

/**
 * Collects the files already stored by every *other* `x-s3-upload` field on
 * the form, as `File` objects ready to hand to `validateAndUploadFilesToS3`
 * as `otherFieldFiles`.
 *
 * The combined-file-size cap is per claim, not per field: the legacy dynamic
 * form aggregates every upload field's files before validating size (see
 * `getFilesFromOtherFields`). Each attachment field here renders as its own
 * isolated control, so without this a schema with two attachment fields could
 * upload past the per-claim limit — each field would only weigh its own files.
 *
 * The sibling fields are read from the root form data via `useJsonForms`,
 * since a control's own props only carry its scoped value. `currentPath` is
 * the current field's property name, excluded so a field never counts itself.
 */
function useOtherAttachmentFiles(currentPath: string): File[] {
  const ctx = useJsonForms();
  const rootData = ctx.core?.data as Record<string, unknown> | undefined;
  const rootSchema = ctx.core?.schema as CloudscapeJsonSchema | undefined;

  return useMemo(() => {
    if (!rootData || !rootSchema) {
      return [];
    }
    const otherFiles: File[] = [];
    for (const name of attachmentFieldNames(rootSchema)) {
      if (name === currentPath) {
        continue;
      }
      const stored = rootData[name];
      if (Array.isArray(stored) && stored.length > 0) {
        otherFiles.push(...transformSerializedFilesToFiles(stored as SerializedFile[]));
      }
    }
    return otherFiles;
  }, [rootData, rootSchema, currentPath]);
}

/**
 * File upload control that stages attachments in S3 as the user picks them,
 * rather than carrying file contents in the form data.
 *
 * Presents the same Cloudscape file upload UI as the library's built-in
 * control and honours the same `x-file-accept` / `x-file-multiple` schema keys,
 * but replaces the upload step: each new file is validated (type, size,
 * filename length, duplicates) and PUT to a presigned S3 URL, and only the
 * resulting `SerializedFile` metadata — including the `s3Key` — is stored in
 * the form data. The claim submission then references the keys instead of
 * inlining megabytes of base64.
 *
 * The whole claim's attachments share one S3 folder, named by the
 * `s3AttachmentsId` the pre-screen mints. Legacy forms use the
 * `amazonClaimReferenceId` for this, which is unavailable here because the
 * backend only assigns it once the claim is created — after the user has
 * already uploaded.
 *
 * Uploads happen eagerly on selection because a presigned PUT cannot be
 * deferred to submit without holding every file in memory. The consequence is
 * that abandoning a form leaves orphaned objects in S3, which is the same
 * trade-off the legacy forms make and is handled by bucket lifecycle rules.
 */
export const S3FileUploadControl = (props: ControlProps) => {
  const { data, handleChange, path, label, errors, visible, schema, required, enabled } = props;
  const { setTouched, isTouched } = useTouched();
  const { s3AttachmentsId, lineOfBusiness } = useS3AttachmentTarget();

  // `path` is the field's location in the form data (its property name for a
  // flat schema); the last segment is the property key `attachmentFieldNames`
  // reports, so the sibling lookup can exclude this field by it.
  const fieldName = path.split(".").pop() ?? path;
  const otherFieldFiles = useOtherAttachmentFiles(fieldName);

  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  // Uploads outlive a fast unmount (wizard step change, navigation), so the
  // completion handler has to know not to touch state afterwards.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const storedFiles = useMemo<SerializedFile[]>(
    () => (Array.isArray(data) ? (data as SerializedFile[]) : []),
    [data]
  );

  // Cloudscape needs File objects. Rebuilt from metadata rather than kept in
  // component state so the tokens survive a reload, where only the persisted
  // metadata is left.
  const displayedFiles = useMemo(() => transformSerializedFilesToFiles(storedFiles), [storedFiles]);

  const fieldSchema = schema as CloudscapeJsonSchema;
  const multiple = fieldSchema["x-file-multiple"] === true;
  const accept = fieldSchema["x-file-accept"] as string | undefined;

  const onFilesChange = useCallback(
    async (selected: File[]) => {
      if (!enabled) {
        return;
      }
      setTouched(path);

      // The control offers no disabled state, so a second selection during an
      // in-flight upload has to be turned away here. Accepting it would race
      // two writes against the same stored list and lose one.
      if (uploading) {
        setUploadErrors([UPLOAD_IN_PROGRESS_ERROR]);
        return;
      }
      if (!s3AttachmentsId || !lineOfBusiness) {
        setUploadErrors([MISSING_TARGET_ERROR]);
        return;
      }

      setUploading(true);
      setUploadErrors([]);
      const result = await validateAndUploadFilesToS3({
        inputFiles: selected,
        existingFiles: storedFiles,
        s3KeyPrefix: s3AttachmentsId,
        lineOfBusiness,
        // Count sibling attachment fields toward the per-claim combined-size
        // cap, matching the legacy flow's aggregation across fields.
        otherFieldFiles,
        acceptedFileTypes: accept,
      });
      if (!mountedRef.current) {
        return;
      }
      setUploading(false);
      setUploadErrors(result.errors);
      // Clearing to undefined rather than [] so a schema that lists this
      // property as required actually reports it missing; AJV's `required`
      // only checks that the key is present, and an empty array satisfies it.
      handleChange(path, result.files.length > 0 ? result.files : undefined);
    },
    [
      accept,
      enabled,
      handleChange,
      lineOfBusiness,
      otherFieldFiles,
      path,
      s3AttachmentsId,
      setTouched,
      storedFiles,
      uploading,
    ]
  );

  if (!visible) {
    return null;
  }

  // Upload and validation failures are shown straight away since they are a
  // direct response to the user's action. Schema errors stay gated on touched,
  // matching every other field in the form.
  const schemaErrorText = isTouched(path) ? errors : undefined;
  const errorText = uploadErrors.length > 0 ? uploadErrors.join(", ") : schemaErrorText;

  return (
    <FormField
      data-testid={`formfield-${path}`}
      label={label}
      description={fieldSchema.description}
      errorText={errorText}
    >
      <SpaceBetween direction="vertical" size="xs">
        <FileUpload
          value={displayedFiles}
          onChange={({ detail }: { detail: FileUploadProps.ChangeDetail }) =>
            onFilesChange([...detail.value])
          }
          multiple={multiple}
          accept={accept}
          showFileSize
          showFileLastModified
          tokenLimit={100}
          ariaRequired={required}
          i18nStrings={{
            uploadButtonText: (many: boolean) => (many ? "Choose files" : "Choose file"),
            dropzoneText: (many: boolean) =>
              many ? "Drop files to upload" : "Drop file to upload",
            removeFileAriaLabel: (fileIndex: number) => `Remove file ${fileIndex + 1}`,
            limitShowFewer: "Show fewer files",
            limitShowMore: "Show more files",
            errorIconAriaLabel: "Error",
          }}
          data-testid={`file-${path}`}
        />
        {uploading ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Spinner variant="disabled" />
            <Box color="text-body-secondary" fontSize="body-m">
              Uploading, please wait...
            </Box>
          </SpaceBetween>
        ) : null}
      </SpaceBetween>
    </FormField>
  );
};

/**
 * Outranks the library's built-in file control (rank 15) so a property opting
 * in via `x-s3-upload` reaches this renderer instead. An explicit higher rank
 * rather than relying on `customRenderers` being searched first, since that
 * ordering is not part of the library's contract.
 */
export const s3FileUploadControlTester = rankWith(
  20,
  schemaMatches((schema) => {
    const candidate = schema as CloudscapeJsonSchema;
    return candidate.type === "array" && candidate[S3_UPLOAD_EXTENSION] === true;
  })
);

export default withJsonFormsControlProps(S3FileUploadControl);
